import { Component, inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { ApiService } from "@bitwarden/common/abstractions/api.service";
import { CryptoService } from "@bitwarden/common/abstractions/crypto.service";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { PlatformUtilsService } from "@bitwarden/common/abstractions/platformUtils.service";
import { UserVerificationService } from "@bitwarden/common/abstractions/userVerification/userVerification.service.abstraction";
import { VerificationType } from "@bitwarden/common/enums/verificationType";
import { SecretVerificationRequest } from "@bitwarden/common/models/request/secret-verification.request";
import {
  ChallengeResponse,
  TwoFactorWebAuthnResponse,
} from "@bitwarden/common/models/response/two-factor-web-authn.response";
import { AuthResponse } from "@bitwarden/common/types/authResponse";
import { StateService } from "@bitwarden/web-vault/app/core";
import { TwoFactorWebauthnDirective } from "@bitwarden/web-vault/app/directives/two-factor-webauthn.directive";
import {
  checkBiometric,
  verify,
  saveCredentials,
} from "@bitwarden/web-vault/app/capacitor-plugins/native-biometric";

// Fixed, non-secret domain-separation input for the WebAuthn PRF extension. This is not a
// secret — it exists only to scope the derived output to this feature, mirroring how a
// standard HKDF "info" parameter is used. Versioned so a future change to the derivation
// scheme can be distinguished from this one.
const PRF_SALT = new TextEncoder().encode("ironfly-gloss-desktop-biometrics-prf-v1").buffer;

export interface TwoFactorWebAuthnDialogData {
  // Set when setup was triggered from the login page, which already verified
  // the user's password moments ago — skips the password re-entry screen below.
  preVerifiedHash?: string | null;
}

@Component({
  selector: "app-two-factor-webauthn",
  templateUrl: "two-factor-webauthn.component.html",
})
export class TwoFactorWebAuthnComponent extends TwoFactorWebauthnDirective implements OnInit {
  // Injected (rather than an @Input assigned on componentInstance after dialog.open()) so it's
  // available from construction time — before ngOnInit branches on it — instead of depending on
  // Angular's change-detection scheduling to land the assignment ahead of the lifecycle hook.
  private readonly dialogData = inject<TwoFactorWebAuthnDialogData | null>(MAT_DIALOG_DATA, {
    optional: true,
  });

  get preVerifiedHash(): string | null | undefined {
    return this.dialogData?.preVerifiedHash;
  }

  private cryptoService: CryptoService = inject(CryptoService);
  private stateService: StateService = inject(StateService);

  constructor(private dialogRef: MatDialogRef<TwoFactorWebAuthnComponent>) {
    super(
      inject(ApiService),
      inject(I18nService),
      inject(PlatformUtilsService),
      inject(LogService),
      inject(UserVerificationService),
    );
  }

  ngOnInit(): void {
    if (this.preVerifiedHash) {
      this.hashedSecret = this.preVerifiedHash;
      // A pre-verified hash only ever comes from the login page's own master-password
      // submission (see BiometricSetupHandoffService) — never OTP/Key-Connector — so this is
      // always the MasterPassword verification type, same as if the step-up dialog had run.
      this.verificationType = VerificationType.MasterPassword;
      this.readKey();
    }
  }

  auth(authResponse: AuthResponse<TwoFactorWebAuthnResponse>) {
    super.auth(authResponse);
    this.name = crypto.randomUUID();
    if (this.authed) {
      this.readKey();
      // Navigation is handled by the afterClosed() subscriber in TwoFactorSetupComponent.manage()
    }
    // todo handle errors
  }

  private get isMobileDevice(): boolean {
    return this.platformUtilsService.isMobileDevice();
  }

  async readKey(): Promise<void> {
    // Both this.readKey() call sites above are fire-and-forget (no await/.catch) — a rejection
    // from either branch (e.g. the native plugin bridge or a keychain call throwing before its
    // own try/catch starts) must not become an unhandled promise rejection that leaves the
    // dialog stuck open with no feedback.
    try {
      await (this.isMobileDevice ? this.readKeyMobile() : this.readKeyDesktop());
    } catch (e) {
      this.logService.error(`Biometric enrollment failed: ${e}`);
      this.notifyBiometricEnrollmentFailed();
    }
  }

  /**
   * Desktop enrollment: fetch a server challenge (also re-verifies the user's master
   * password), create a platform-authenticator credential requesting the WebAuthn PRF
   * extension, then immediately derive the PRF secret via a get() ceremony. The PRF output is
   * real per-credential secret material computed inside the authenticator's secure hardware —
   * unlike raw authenticatorData (rpIdHash), it cannot be recomputed from public information,
   * so it's what actually gates decryption of the locally-stored master key.
   *
   * Server-side WebAuthn registration (submit()) is intentionally skipped: this credential is
   * never used to authenticate against the server, only to gate a local secret.
   */
  private async readKeyDesktop(): Promise<void> {
    // Desktop has no OS-level keychain ACL to fall back on the way mobile's Capacitor keychain
    // does, so — unlike readKeyMobile — this never falls back to the in-memory session key:
    // doing so would silently persist a key to disk for users who explicitly chose a vault
    // timeout other than "Never". Check this first, before any biometric prompt, so a user
    // who hasn't opted in isn't asked to authenticate twice for nothing.
    const masterKeyAuto = await this.stateService.getCryptoMasterKeyAuto();
    if (!masterKeyAuto) {
      this.notifyAndClose("biometrics", this.i18nService.t("biometricRequiresNeverLockTimeout"));
      return;
    }

    if (
      typeof PublicKeyCredential === "undefined" ||
      typeof PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable !== "function" ||
      !(await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable())
    ) {
      this.notifyBiometricUnsupported();
      return;
    }

    let challenge: ChallengeResponse;
    try {
      const request = await this.buildRequestModel(SecretVerificationRequest);
      challenge = await this.apiService.getBiometricsWebAuthnChallenge(request);
    } catch (e) {
      this.notifyAndClose(
        "webauthn",
        (e as Error)?.message ?? this.i18nService.t("unexpectedError"),
      );
      return;
    }

    // Override the server-issued rp.id with the page's actual current origin. This ceremony's
    // result is never submitted back to the server for verification (see the doc comment
    // above) — the server-provided value only exists to satisfy the browser's
    // own same-origin check, so it must always equal the current origin. Trusting a
    // server-issued value here is an unnecessary dependency: if it ever drifted from the
    // domain the app is actually served on, this would fail closed for everyone (and does today
    // in local development, since the shared identity service issues a fixed rp.id unrelated to
    // localhost). window.location.hostname can never mismatch, by construction.
    const rpId = window.location.hostname;

    let credential: PublicKeyCredential;
    try {
      credential = (await navigator.credentials.create({
        publicKey: {
          ...challenge,
          rp: { ...challenge.rp, id: rpId },
          // excludeCredentials from the server is the user's already-registered 2FA keys for
          // this same endpoint's server-verified flow. Carrying that list into this ceremony
          // would make create() throw InvalidStateError for a user who registered 2FA WebAuthn
          // on the same platform authenticator they're now enrolling for biometrics — an
          // unrecoverable, unrelated failure. This ceremony's result is never submitted back to
          // the server (see the doc comment above), so nothing here needs to be excluded.
          excludeCredentials: [],
          // Same reasoning as rp.id above: the server's authenticatorSelection
          // (userVerification: "discouraged") is a suggestion for the server-verified 2FA
          // flow this challenge endpoint was originally built for, not something we're bound
          // to honor here. "Discouraged" measurably prevents some platform authenticators
          // (confirmed: Windows Hello) from negotiating the PRF/hmac-secret extension at all,
          // even when the hardware/browser fully support it — requiring verification is also
          // strictly more secure, and matches what the get() calls below already require.
          //
          // residentKey/requireResidentKey: the created credential's rawId isn't persisted
          // anywhere (see get() fallback below), so the unlock-time get() ceremony has no
          // allowCredentials to target — it can only find this credential if it's discoverable.
          //
          // authenticatorAttachment: without this, Chrome/Edge still offer "use a phone or
          // tablet" (hybrid/caBLE) and external security keys alongside the platform
          // authenticator the availability check above already confirmed — enrolling via phone
          // would make "desktop biometrics" mean scanning a QR code on every unlock.
          authenticatorSelection: {
            ...challenge.authenticatorSelection,
            userVerification: "required",
            residentKey: "required",
            requireResidentKey: true,
            authenticatorAttachment: "platform",
          },
          // Evaluate PRF directly at create() (not just an empty {} feature-detect request):
          // some CTAP 2.2+ authenticators only reliably report prf.enabled when an actual
          // eval payload is present, and doing so lets those same authenticators return the
          // secret immediately, skipping the separate get() ceremony below entirely. Older
          // (pre-2.2) authenticators may still only report `enabled` here without a value —
          // the get() fallback below covers that case.
          extensions: {
            ...challenge.extensions,
            prf: { eval: { first: PRF_SALT } },
          } as any,
        },
      })) as PublicKeyCredential;
    } catch (e) {
      // Availability was already confirmed above, so a failure here is a ceremony-level
      // problem (user cancellation, timeout, NotAllowedError) rather than "not supported" —
      // don't mislabel it as such.
      this.logService.error(`Desktop biometric enrollment failed: ${e}`);
      this.notifyBiometricEnrollmentFailed();
      return;
    }

    const createPrfResult = (credential.getClientExtensionResults() as any).prf;
    if (createPrfResult?.enabled !== true) {
      this.logService.warning(
        "Desktop biometric enrollment aborted: this authenticator/browser does not support the WebAuthn PRF extension.",
      );
      await this.clearKeyAndFail(() => this.notifyBiometricUnsupported());
      return;
    }

    let secret: ArrayBuffer = createPrfResult.results?.first ?? null;

    if (!secret) {
      // Older (pre-CTAP 2.2) authenticator: enabled was reported at create(), but the actual
      // value isn't available until a follow-up get() ceremony.
      try {
        const assertion = (await navigator.credentials.get({
          publicKey: {
            challenge: crypto.getRandomValues(new Uint8Array(32)),
            rpId,
            allowCredentials: [{ id: credential.rawId, type: "public-key" }],
            userVerification: "required",
            extensions: { prf: { eval: { first: PRF_SALT } } } as any,
          },
        })) as PublicKeyCredential;
        secret = (assertion.getClientExtensionResults() as any).prf?.results?.first ?? null;
      } catch (e) {
        this.logService.error(`Failed to derive PRF secret for desktop biometrics: ${e}`);
        await this.clearKeyAndFail(() => this.notifyBiometricEnrollmentFailed());
        return;
      }
    }

    if (!secret) {
      this.logService.error("Desktop biometric enrollment aborted: PRF secret unavailable.");
      await this.clearKeyAndFail(() => this.notifyBiometricEnrollmentFailed());
      return;
    }

    try {
      await this.cryptoService.setBiometricsKey(secret, undefined, masterKeyAuto);
      await this.stateService.setBiometricFingerprintValidated(true);
      this.dialogRef.close(true);
    } catch (e) {
      this.logService.error(`Error setting biometrics key ${e}`);
      await this.clearKeyAndFail(() => this.notifyBiometricEnrollmentFailed());
    }
  }

  // create() may have silently replaced a previously-enrolled discoverable credential, so any
  // failure past this point leaves cryptoBiometricKeyAuto wrapped under a secret that no longer
  // exists. Clear it rather than leaving an undecryptable blob behind.
  private async clearKeyAndFail(notify: () => void): Promise<void> {
    await this.cryptoService.clearBiometricsKey();
    notify();
  }

  private notifyAndClose(category: string, message: string): void {
    this.confirmationDialogService.notify("error", category, message, false);
    this.dialogRef.close(false);
  }

  private notifyBiometricUnsupported(): void {
    this.notifyAndClose("biometrics", this.i18nService.t("biometricNotSupported"));
  }

  private notifyBiometricEnrollmentFailed(): void {
    this.notifyAndClose("biometrics", this.i18nService.t("biometricEnrollmentFailed"));
  }

  private async readKeyMobile(): Promise<void> {
    const available = await checkBiometric();
    if (!available.isAvailable) {
      this.confirmationDialogService.notify(
        "error",
        "biometrics",
        this.i18nService.t("biometricNotSupported"),
        false,
      );
      this.dialogRef.close();
      return;
    }

    const hashedSecret = this.hashedSecret;

    try {
      const masterKeyAuto = await this.resolveMasterKeyForBiometrics();
      if (!masterKeyAuto) {
        this.logService.error("Cannot save biometric credential: master key unavailable.");
        this.dialogRef.close(false);
        return;
      }

      const email = await this.stateService.getEmail();
      await verify();
      await saveCredentials(email, masterKeyAuto + ":" + hashedSecret);
      this.dialogRef.close(true);
    } catch (e) {
      this.logService.error(e);
      this.dialogRef.close(false);
    }
  }

  // getCryptoMasterKeyAuto is only stored when vaultTimeout == null ("Never"). For all other
  // timeout settings fall back to the in-memory session key.
  private async resolveMasterKeyForBiometrics(): Promise<string | null> {
    const masterKeyAuto = await this.stateService.getCryptoMasterKeyAuto();
    if (masterKeyAuto) {
      return masterKeyAuto;
    }
    const inMemoryKey = await this.stateService.getCryptoMasterKey();
    return inMemoryKey?.keyB64 ?? null;
  }
}
